/**
 * EmbeddedHostChannel — a "cola" do transporte host ↔ Efflux
 * ==========================================================
 *
 * Liga:
 * - Host → Efflux: instala `window.__efflux.dispatch(raw)` → {@link EmbeddedCommandRouter}.
 * - Efflux → Host: envia eventos via `window.EffluxHost.emit(json)`
 *   (@JavascriptInterface injetado pelo host Android).
 *
 * Handshake nos DOIS sentidos:
 * - `markPlayerReady()` (o bootstrap chama quando as stores estão ligadas) →
 *   drena a fila de comandos e emite o evento `ready` pro host.
 * - `window.__efflux.hostReady()` (o host chama quando injetou `EffluxHost` +
 *   registrou listeners) → drena o buffer de eventos (inclui o `ready`).
 *
 * Assim nenhum comando/evento se perde independente de quem inicializa primeiro
 * (R2.1 + R2.4). Transporte injetável → testável sem WebView.
 */
import { EmbeddedCommandRouter } from './EmbeddedCommandRouter';
import { EmbeddedEventEmitter, EventTransport } from './EmbeddedEventEmitter';
import { EmbeddedCommandHandlers } from './commandHandlers';
import { EventPayloadMap, EventType, makeEvent } from './protocol';

/** Superfície mínima que o host Android injeta no `window` (R2.3 — só o necessário). */
interface EffluxHostBridge {
  setDeviceToken?(token: string): void | Promise<void>;
  emit(json: string): void | Promise<void>;
  /**
   * Path 3 — recebe o frame de cover (JPEG base64, sem prefixo `data:`) que o
   * player capturou a pedido do host (comando `captureCoverFrame`). Opcional:
   * hosts/AAR antigos não expõem → o player degrada gracioso (não quebra).
   */
  onCoverFrame?(base64: string): void | Promise<void>;
  /**
   * Pede ao host um check de OTA do bundle AGORA (o admin clicou "Forçar update
   * agora" → cmd `ota:check` via SSE). No embedded quem baixa e troca o bundle é
   * o AAR, não o JS — sem esta ponte o comando não teria como chegar no updater
   * nativo e só restaria esperar o ciclo de 1 h.
   *
   * Opcional de propósito: AAR anterior a 1.0.444 não expõe. Quem chama DEVE
   * testar `typeof === 'function'` e reportar `unsupported` quando ausente —
   * responder sucesso sem ter encaminhado nada daria falso positivo no admin.
   */
  requestOtaCheck?(): void | Promise<void>;
}

interface EffluxWindowApi {
  /** Host → Efflux: entrega um envelope de comando (string JSON ou objeto). */
  dispatch(raw: unknown): void;
  /** Host sinaliza que está pronto pra receber eventos. */
  hostReady(): void;
}

declare global {
  interface Window {
    __efflux?: EffluxWindowApi;
    __effluxEmbedded?: unknown;
    EffluxHost?: EffluxHostBridge;
  }
}

export class EmbeddedHostChannel {
  private router: EmbeddedCommandRouter | null = null;
  private emitter: EmbeddedEventEmitter | null = null;
  /** Transporte ao vivo (bridge) — usado pelo heartbeat, que NÃO deve bufferizar. */
  private liveSend: EventTransport | null = null;
  private installed = false;

  /**
   * Inicializa o canal com os handlers reais e instala a API no `window`.
   * Idempotente — chamar 2× não reinstala nem recria.
   */
  init(handlers: EmbeddedCommandHandlers, transport?: EventTransport): void {
    if (this.installed) return;

    const send: EventTransport =
      transport ??
      ((json) => {
        void Promise.resolve(window.EffluxHost?.emit(json)).catch(
          () => undefined,
        );
      });

    this.liveSend = send;
    this.emitter = new EmbeddedEventEmitter(send);
    this.router = new EmbeddedCommandRouter(handlers);

    const api: EffluxWindowApi = {
      dispatch: (raw) => this.router?.dispatch(raw),
      hostReady: () => this.emitter?.markReady(),
    };
    if (typeof window !== 'undefined') {
      window.__efflux = api;
    }
    this.installed = true;
  }

  /** Emite um evento tipado pro host (bufferizado até o host ficar pronto). */
  emit<T extends EventType>(type: T, payload?: EventPayloadMap[T]): void {
    this.emitter?.emit(type, payload);
  }

  /**
   * Fase 9 — heartbeat pro watchdog do AAR. Enviado DIRETO na bridge (fora do buffer
   * de eventos): um batimento é fire-and-forget — represar batimentos antigos e
   * drená-los em lote enganaria o watchdog; um batimento perdido antes do host ficar
   * pronto é irrelevante (o próximo vem em ~5s). Nunca lança (não derruba o player).
   */
  emitHeartbeat(ts: number): void {
    try {
      this.liveSend?.(JSON.stringify(makeEvent('heartbeat', { ts })));
    } catch {
      /* transporte indisponível — ignora; o próximo batimento tenta de novo */
    }
  }

  /**
   * O player está pronto (stores ligadas): drena comandos enfileirados e avisa o
   * host com o evento `ready`. Passe o `externalId`/`version` p/ o payload.
   */
  markPlayerReady(payload: EventPayloadMap['ready']): void {
    this.router?.markReady();
    this.emit('ready', payload);
  }

  /** Só p/ teste/diagnóstico. */
  isInstalled(): boolean {
    return this.installed;
  }

  /** Reset — usado em teste (limpa a API do window). */
  reset(): void {
    if (typeof window !== 'undefined' && window.__efflux) {
      delete window.__efflux;
    }
    this.router = null;
    this.emitter = null;
    this.liveSend = null;
    this.installed = false;
  }
}

/** Singleton do canal (um WebView = uma instância do player = um canal). */
export const embeddedHostChannel = new EmbeddedHostChannel();
